import React, { useContext } from 'react';
import { Button as TpaButton, TextButton, HoverStyles, TextButtonPriority } from 'wix-ui-tpa/cssVars';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { withClientSideRenderMark } from '../../../../contexts/ClientSideRenderMark';
import { CommonProps, RestrictedProps } from '../../../../types/common';
import { classes } from './Restricted.st.css';

interface RestrictedViewConfiguration {
  title: string;
  button: string;
  link: string;
  onButtonClick(): void;
  onLinkClick(): void;
}

const RestrictedTextsContext = React.createContext<RestrictedViewConfiguration>({
  title: '',
  button: '',
  link: '',
  onButtonClick() {},
  onLinkClick() {},
});

const Restricted: React.FC<RestrictedProps & CommonProps> = ({
  missingPlan,
  navigateToHomePage,
  switchAccounts,
  navigateToPlanList,
}) => {
  const { t } = useTranslation();
  const { isMobile, isRTL, isEditor } = useEnvironment();

  const texts = React.useMemo<RestrictedViewConfiguration>(() => {
    return missingPlan || isEditor
      ? {
          title: t('restricted.title'),
          button: t('restricted.check-plans'),
          link: t('restricted.back'),
          onButtonClick: navigateToPlanList,
          onLinkClick: navigateToHomePage,
        }
      : {
          title: t('restricted.other-title'),
          button: t('restricted.back-to-site'),
          link: t('restricted.switch-account'),
          onButtonClick: navigateToHomePage,
          onLinkClick: switchAccounts,
        };
  }, [missingPlan, isEditor, navigateToPlanList, navigateToHomePage, switchAccounts, t]);

  return (
    <RestrictedTextsContext.Provider value={texts}>
      <div dir={isRTL ? 'rtl' : 'ltr'} data-hook="restricted-app" className={classes.wrapper}>
        {isMobile ? <Mobile /> : <Desktop />}
      </div>
    </RestrictedTextsContext.Provider>
  );
};

const Mobile: React.FC = () => (
  <div className={classes.mobileContainer}>
    <Title maxWidth={260} fontSize="28px" />
    <div className={classes.mobileButton}>
      <Button />
    </div>
    <div className={classes.mobileLink}>
      <Link />
    </div>
  </div>
);

const Desktop: React.FC = () => (
  <div className={classes.desktopContainer}>
    <Title maxWidth={600} fontSize="46px" />
    <div className={classes.desktopButton}>
      <Button />
    </div>
    <div className={classes.desktopLink}>
      <Link />
    </div>
  </div>
);

interface TitleProps {
  maxWidth: number;
  fontSize: string;
}

const Title: React.FC<TitleProps> = ({ maxWidth, fontSize }) => {
  const { title } = useContext(RestrictedTextsContext);
  return (
    <div className={classes.title} style={{ maxWidth, width: maxWidth, fontSize }} data-hook="restricted-title">
      {title}
    </div>
  );
};

const Button: React.FC = () => {
  const { button, onButtonClick } = useContext(RestrictedTextsContext);

  return (
    <TpaButton
      data-hook="restricted-button"
      onClick={() => onButtonClick()}
      hoverStyle={HoverStyles.underline}
      className={classes.tpaButton}
    >
      {button}
    </TpaButton>
  );
};

const Link: React.FC = () => {
  const { link, onLinkClick } = useContext(RestrictedTextsContext);

  return (
    <TextButton
      onClick={() => onLinkClick()}
      priority={TextButtonPriority.link}
      hoverStyle={HoverStyles.underline}
      data-hook="restricted-link"
    >
      {link}
    </TextButton>
  );
};

export default withClientSideRenderMark(Restricted);
